import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ResponsiveDialog from '../common/ResponsiveDialog';
import pettyCashApi from '../../services/pettyCashApi';
import type { AuxiliaryList, CashAccountOption, PettyCashHolder } from '../../types/pettyCash';
import { formatAmount, parseAmount } from '../../utils/pettyCash';

/**
 * A petty cash holder: the PRPO user, the SAP cash account his receipts are paid from and, on a shared
 * account, his auxiliary (SAP Dimension 5 code of the account's type, e.g. PC00004 RIYADH SHIYAS on
 * 123020100 "P Cash"): SAP tells the holders' cash apart by it.
 */

export interface HolderInput {
  holderId?: number;
  userId: number;
  sapAccount: string;
  auxiliaryCode: string | null;
  floatAmount: number | null;
  isActive: boolean;
}

interface Props {
  open: boolean;
  companyId: number;
  currency: string;
  holder: PettyCashHolder | null;
  holders: PettyCashHolder[];
  users: Array<{ userId: number; name: string; email: string | null }>;
  onClose: () => void;
  onSave: (input: HolderInput) => Promise<void>;
}

type AuxOption = { code: string; name: string };

const HolderDialog: React.FC<Props> = ({ open, companyId, currency, holder, holders, users, onClose, onSave }) => {
  const [userId, setUserId] = useState<number | ''>('');
  const [account, setAccount] = useState('');
  const [auxCode, setAuxCode] = useState<string | null>(null);
  const [floatText, setFloatText] = useState('');
  const [active, setActive] = useState(true);
  const [accounts, setAccounts] = useState<CashAccountOption[] | null>(null);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [aux, setAux] = useState<AuxiliaryList | null>(null);
  const [auxLoading, setAuxLoading] = useState(false);
  const [auxError, setAuxError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setUserId(holder?.userId ?? '');
    setAccount(holder?.sapAccount ?? '');
    setAuxCode(holder?.auxiliaryCode ?? null);
    setFloatText(holder?.floatAmount ? String(holder.floatAmount) : '');
    setActive(holder?.isActive ?? true);
    setError(null);
    setBusy(false);
    if (!accounts) {
      pettyCashApi.cashAccounts(companyId)
        .then(setAccounts)
        .catch((e: any) => { setAccounts([]); setAccountsError(e?.response?.data?.message || 'The SAP cash accounts could not be read'); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, holder]);

  const chosen = accounts?.find((a) => a.code === account) ?? null;
  const takesAux = !!chosen && (!!chosen.auxType || !!chosen.auxMandatory);

  // The auxiliaries of the chosen account's type (read when an account that takes them is chosen).
  useEffect(() => {
    if (!open || !account || !takesAux) { setAux(null); setAuxError(null); return undefined; }
    let cancelled = false;
    setAuxLoading(true);
    setAuxError(null);
    pettyCashApi.auxiliaries(companyId, { account })
      .then((list) => { if (!cancelled) setAux(list); })
      .catch((e: any) => { if (!cancelled) { setAux(null); setAuxError(e?.response?.data?.message || 'The auxiliaries could not be read from SAP'); } })
      .finally(() => { if (!cancelled) setAuxLoading(false); });
    return () => { cancelled = true; };
  }, [open, companyId, account, takesAux]);

  const taken = new Set(holders.filter((h) => h.holderId !== holder?.holderId).map((h) => h.userId));
  const others = holders.filter((h) => h.holderId !== holder?.holderId && h.isActive && h.sapAccount === account);
  const usedBy = new Map(others.filter((h) => h.auxiliaryCode).map((h) => [h.auxiliaryCode as string, h.userName]));
  const auxOptions: AuxOption[] = aux?.codes ?? [];
  const auxValue: AuxOption | null = auxCode ? auxOptions.find((o) => o.code === auxCode) ?? { code: auxCode, name: holder?.auxiliaryCode === auxCode ? holder?.auxiliaryName || '' : '' } : null;
  const typeLabel = aux?.typeName ? `${aux.typeName}${aux.type && aux.type !== aux.typeName ? ` (${aux.type})` : ''}` : chosen?.auxType || '';

  const save = async () => {
    if (!userId) { setError('Choose the user.'); return; }
    if (!account) { setError('Choose the SAP cash account.'); return; }
    if (chosen?.auxMandatory && !auxCode) { setError(`Choose the holder's auxiliary: ${account} needs one.`); return; }
    if (auxCode && active && usedBy.has(auxCode)) { setError(`${auxCode} is already ${usedBy.get(auxCode)}'s auxiliary on ${account}.`); return; }
    const amount = floatText.trim() ? parseAmount(floatText) : null;
    if (floatText.trim() && (amount === null || amount < 0)) { setError('The float is not an amount.'); return; }
    setBusy(true);
    setError(null);
    try {
      await onSave({
        holderId: holder?.holderId,
        userId: Number(userId),
        sapAccount: account,
        auxiliaryCode: takesAux || auxCode ? auxCode : null,
        floatAmount: amount,
        isActive: active,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'The holder could not be saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth aria-labelledby="pc-holder-title">
      <DialogTitle id="pc-holder-title">{holder ? `Holder: ${holder.userName}` : 'Add a petty cash holder'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <TextField id="pc-holder-user" select label="User" value={userId} onChange={(e) => setUserId(Number(e.target.value) || '')} disabled={!!holder} required fullWidth helperText="Users who can open this company">
            {users.filter((u) => !taken.has(u.userId) || u.userId === holder?.userId).map((u) => (
              <MenuItem key={u.userId} value={u.userId}>{u.name}{u.email ? ` · ${u.email}` : ''}</MenuItem>
            ))}
          </TextField>
          {accounts === null ? (
            <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Reading the SAP cash accounts…</Typography>
          ) : (
            <TextField
              id="pc-holder-account"
              select
              label="SAP petty cash account"
              value={account}
              onChange={(e) => { setAccount(e.target.value); setAuxCode(null); }}
              required
              fullWidth
              error={!!accountsError}
              helperText={accountsError || 'A G/L account SAP flags as a cash account (the bank accounts are flagged too: pick the petty cash one)'}
            >
              {account && !accounts.some((a) => a.code === account) ? <MenuItem value={account}>{account} {holder?.sapAccountName || ''}</MenuItem> : null}
              {accounts.map((a) => (
                <MenuItem key={a.code} value={a.code}>
                  {a.code} {a.name}
                  {a.auxType ? ` · auxiliaries ${a.auxType}` : ''}
                  {a.balance !== null ? ` · balance ${formatAmount(a.balance)} ${currency}` : ''}
                </MenuItem>
              ))}
            </TextField>
          )}
          {takesAux ? (
            auxLoading ? (
              <Typography variant="body2" color="text.secondary"><CircularProgress size={14} sx={{ mr: 1 }} />Reading the auxiliaries from SAP…</Typography>
            ) : (
              <Autocomplete
                id="pc-holder-aux"
                options={auxOptions}
                value={auxValue}
                onChange={(_e, v) => setAuxCode(v?.code ?? null)}
                isOptionEqualToValue={(a, b) => a.code === b.code}
                getOptionLabel={(o) => `${o.code}${o.name ? ` ${o.name}` : ''}`}
                getOptionDisabled={(o) => usedBy.has(o.code)}
                renderOption={(props, o) => (
                  <li {...props} key={o.code}>
                    {o.code} {o.name}{usedBy.has(o.code) ? ` · ${usedBy.get(o.code)}` : ''}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`Auxiliary (Dimension 5)${typeLabel ? `: ${typeLabel}` : ''}`}
                    required={!!chosen?.auxMandatory}
                    error={!!auxError}
                    helperText={auxError || `The holder's code on ${account}: SAP keeps every holder's cash on this account and tells them apart by it. His receipts are paid by a journal entry that carries it.`}
                  />
                )}
              />
            )
          ) : null}
          {!takesAux && others.length ? (
            <Alert severity="warning">This account is also used by {others.map((h) => h.userName).join(', ')}: SAP then keeps one balance for all of them. One account per holder is recommended.</Alert>
          ) : null}
          <TextField id="pc-holder-float" label={`Float ${currency}`.trim()} value={floatText} onChange={(e) => setFloatText(e.target.value)} inputProps={{ inputMode: 'decimal' }} helperText="The amount the holder is topped up to (optional)" fullWidth />
          <FormControlLabel control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />} label="Active (an inactive holder keeps the history but cannot claim)" />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => { void save(); }} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default HolderDialog;
